/**
 * `placeFloating()` (ui-ux §19.7): places a floating box (tooltip, coach bubble) next to an anchor rect inside the
 * viewport minus the safe insets and a pad. Pure (rects in, rect out) so it is unit-tested; the DOM wrapper lives in
 * `components/overlays.ts`. Owner: UI/Visual (F3).
 *
 * 1. Preferred side: top for touch (the finger covers below), bottom for mouse.
 * 2. Flip when it does not fit.
 * 3. Shift horizontally to stay inside; the arrow x is clamped to [12, w − 12].
 */
export interface Rect { x: number; y: number; w: number; h: number }
export interface Bounds { w: number; h: number; safe?: { t: number; r: number; b: number; l: number } }
export interface Placement { x: number; y: number; side: 'top' | 'bottom'; arrowX: number }

export function placeFloating(anchor: Rect, size: { w: number; h: number }, view: Bounds,
  opts: { prefer?: 'top' | 'bottom'; gap?: number; pad?: number } = {}): Placement {
  const gap = opts.gap ?? 8, pad = opts.pad ?? 8;
  const s = view.safe ?? { t: 0, r: 0, b: 0, l: 0 };
  const minX = s.l + pad, maxX = view.w - s.r - pad, minY = s.t + pad, maxY = view.h - s.b - pad;
  const topY = anchor.y - gap - size.h;
  const botY = anchor.y + anchor.h + gap;
  const fitsTop = topY >= minY, fitsBot = botY + size.h <= maxY;
  let side: 'top' | 'bottom' = opts.prefer ?? 'top';
  if (side === 'top' && !fitsTop && fitsBot) side = 'bottom';
  else if (side === 'bottom' && !fitsBot && fitsTop) side = 'top';
  else if (!fitsTop && !fitsBot) side = (anchor.y - minY) > (maxY - anchor.y - anchor.h) ? 'top' : 'bottom';
  let y = side === 'top' ? topY : botY;
  y = Math.max(minY, Math.min(y, maxY - size.h));
  const cx = anchor.x + anchor.w / 2;
  let x = cx - size.w / 2;
  x = Math.max(minX, Math.min(x, maxX - size.w));
  if (size.w > maxX - minX) x = minX;
  const arrowX = Math.max(12, Math.min(size.w - 12, cx - x));
  return { x: Math.round(x), y: Math.round(y), side, arrowX: Math.round(arrowX) };
}
